// src/pages/billing/PriceDetailPage.tsx

import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useBillingPrice, useI18n } from '@react/hooks';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Alert,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  ErrorAlert,
} from '@react/components';

function formatMoney(money: any): string {
  if (!money) return '--';
  const amount = (money.amount ?? 0) / Math.pow(10, money.decimal_places ?? 2);
  return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatDate(dateStr: string | undefined | null): string {
  if (!dateStr) return '--';
  try { return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return String(dateStr); }
}

function enumLabel(value: string, prefix: string): string {
  return (value || '').replace(prefix, '').replace(/_/g, ' ');
}

export const PriceDetailPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = useBillingPrice();
  const { t } = useI18n();
  const item = store.currentItem as any;

  useEffect(() => {
    if (id) store.GetPrice(id);
    return () => { store.clearCurrent(); };
  }, [id]);

  if (store.isLoading) {
    return <div className="flex items-center justify-center h-64"><div className="text-muted-foreground">{t('common.loading')}</div></div>;
  }
  if (store.error) {
    return <><ErrorAlert error={store.error} /><Link to="/billing/products" className="mt-2 inline-block text-sm underline text-primary mt-2 inline-block text-sm underline text-primary">{t("common.backToList")}</Link></>;
  }
  if (!item) {
    return <Alert variant="warning" title={t('common.notFound')}><Link to="/billing/products" className="mt-2 inline-block text-sm underline text-primary">{t('common.backToList')}</Link></Alert>;
  }

  return (
    <div className="space-y-6">
      <nav className="text-sm">
        <Link to="/billing" className="text-muted-foreground hover:text-foreground">Billing</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/billing/products" className="text-muted-foreground hover:text-foreground">Products</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">Price Details</span>
      </nav>

      {/* The tab already names this page; the heading stays for screen readers. */}
      <div className="flex items-center gap-3">
        <h1 className="sr-only">Price Details</h1>
        {item.is_active ? <Badge variant="success" size="sm">Active</Badge> : <Badge variant="secondary" size="sm">Inactive</Badge>}
      </div>

      <Card>
        <CardHeader><CardTitle>Pricing Configuration</CardTitle></CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Model</dt>
              <dd className="text-sm text-foreground col-span-2">{enumLabel(item.model, 'PRICING_MODEL_')}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Currency</dt>
              <dd className="text-sm text-foreground col-span-2">{enumLabel(item.currency, 'CURRENCY_')}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Billing Period</dt>
              <dd className="text-sm text-foreground col-span-2">{enumLabel(item.billing_period, 'BILLING_PERIOD_')}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Unit Amount</dt>
              <dd className="text-sm text-foreground col-span-2 font-bold">{formatMoney(item.unit_amount)}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Usage Type</dt>
              <dd className="text-sm text-foreground col-span-2">{String(item.usage_type ?? '--')}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Aggregate Usage</dt>
              <dd className="text-sm text-foreground col-span-2">{String(item.aggregate_usage ?? '--')}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Trial Days</dt>
              <dd className="text-sm text-foreground col-span-2">{String(item.trial_days ?? '0')}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Created</dt>
              <dd className="text-sm text-foreground col-span-2">{formatDate(item.created_at)}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Tiers */}
      {item.tiers?.tiers?.length > 0 && (
        <Card>
          <CardHeader><CardTitle>Pricing Tiers</CardTitle></CardHeader>
          <CardContent>
            <Table striped>
              <TableHeader>
                <TableRow>
                  <TableHead>Up To</TableHead>
                  <TableHead>Unit Amount</TableHead>
                  <TableHead>Flat Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {item.tiers.tiers.map((tier: any, i: number) => (
                  <TableRow key={i}>
                    <TableCell>{tier.up_to === 0 ? 'Unlimited' : String(tier.up_to)}</TableCell>
                    <TableCell>{formatMoney(tier.unit_amount)}</TableCell>
                    <TableCell>{formatMoney(tier.flat_amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
});

export default PriceDetailPage;
